import {
    Activity,
    ActivityEntryFormGroup,
    ActivityEntrySubGroup,
    KPI,
} from "@features/report/activity-entry/types/activityEntry";

export type ActivityContext = {
    group: ActivityEntryFormGroup;
    activity: Activity;
    subGroup: ActivityEntrySubGroup | null;
    subGroupId: number | null;
    subGroupName: string | null;
};

export type ActivityKpiContext = ActivityContext & {
    kpi: KPI;
};

export const getActivityAccordionKey = (
    subGroupId: number | null,
    activityId: number
): string => `${subGroupId ?? "base"}:${activityId}`;

export const getKpiValueKey = (
    subGroupId: number | null,
    activityId: number,
    kpiId: number
): string => `${subGroupId ?? "base"}.${activityId}.${kpiId}`;

export const sortSubGroups = (
    subGroups?: ActivityEntrySubGroup[]
): ActivityEntrySubGroup[] =>
    (subGroups ?? [])
        .slice()
        .sort(
            (left, right) =>
                (left.sub_group_list_order ?? 0) - (right.sub_group_list_order ?? 0)
        );

const sortActivities = (
    activities: Activity[] | undefined,
    showInactive: boolean
): Activity[] =>
    (activities ?? [])
        .filter((activity) => (showInactive ? true : activity.activity_is_active))
        .slice()
        .sort(
            (left, right) =>
                (left.activity_list_order ?? 0) - (right.activity_list_order ?? 0)
        );

const sortKpis = (kpis: KPI[] | undefined, showInactive: boolean): KPI[] =>
    (kpis ?? [])
        .filter((kpi) => (showInactive ? true : kpi.kpi_is_active))
        .slice()
        .sort((left, right) => (left.kpi_list_order ?? 0) - (right.kpi_list_order ?? 0));

export const getGroupActivityContexts = (
    group: ActivityEntryFormGroup,
    showInactive: boolean
): ActivityContext[] => {
    const useSubGroups =
        Boolean(group.has_subgroups) || (group.sub_groups?.length ?? 0) > 0;

    if (useSubGroups) {
        return sortSubGroups(group.sub_groups).flatMap((subGroup) =>
            sortActivities(subGroup.activities, showInactive).map((activity) => ({
                group,
                activity,
                subGroup,
                subGroupId: subGroup.report_sub_group_id ?? null,
                subGroupName: subGroup.sub_group_name ?? null,
            }))
        );
    }

    return sortActivities(group.activities, showInactive).map((activity) => ({
        group,
        activity,
        subGroup: null,
        subGroupId: null,
        subGroupName: null,
    }));
};

export const getAllActivityContexts = (
    groups: ActivityEntryFormGroup[] | undefined,
    showInactive: boolean
): ActivityContext[] =>
    (groups ?? []).flatMap((group) => getGroupActivityContexts(group, showInactive));

export const getActivityKpiContexts = (
    context: ActivityContext,
    showInactive: boolean
): ActivityKpiContext[] =>
    sortKpis(context.activity.kpis, showInactive).map((kpi) => ({
        ...context,
        kpi,
    }));

export const getAllKpiContexts = (
    groups: ActivityEntryFormGroup[] | undefined,
    showInactive: boolean
): ActivityKpiContext[] =>
    getAllActivityContexts(groups, showInactive).flatMap((context) =>
        getActivityKpiContexts(context, showInactive)
    );
